<script setup>
import { computed, onMounted, ref, watch } from "vue";
import axios from "axios";
import { useStore } from "vuex";
import { useToast } from "vue-toast-notification";
import LoadingSpinner from "../../components/LoadingSpinner.vue";
import { scopeSections, toCurrency } from "../../assets/js/helpers";
import TradesFilter from "./TradesFilter.vue";

const props = defineProps({
  propertyId: {
    type: [String, Number],
    required: true,
  },
  propertyMetadata: {
    type: Object,
    default: () => ({}),
  },
  setActions: {
    type: Function,
  },
});

const loading = ref(false);
const scopeObj = ref({});
const activeView = ref("overview");
const selectedTrade = ref("all");
const viewMoreState = ref({});

const store = useStore();
const toast = useToast();

const apiEndpoint = store.state.apiEndpoint;
const token = sessionStorage.getItem("token");
const headers = {
  headers: {
    Authorization: `Bearer ${token}`,
  },
};

const NO_TRADE_KEY = "no-trade";
const NO_TRADE_LABEL = "No Trades Assigned";
const ITEMS_LIMIT = 10;

const cleanupTextFormat = (text) => {
  if (!text) return "";
  return text
    .replace(/[\n\r]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const sectionTitleMap = computed(() => {
  const map = {};
  scopeSections.forEach((section) => {
    map[section.id] = section.title;
  });

  if (props.propertyMetadata?.sections) {
    Object.entries(props.propertyMetadata.sections).forEach(([id, section]) => {
      map[id] = section.schedule2_title || section.title || map[id] || id;
    });
  }
  return map;
});

const subsectionTitleMap = computed(() => {
  const map = {};
  scopeSections.forEach((section) => {
    (section.items || []).forEach((item) => {
      map[item.id] = item.title || item.schedule2_title || item.id;
    });
  });

  if (props.propertyMetadata?.sections) {
    Object.values(props.propertyMetadata.sections).forEach((section) => {
      (section.items || []).forEach((item) => {
        map[item.id] = item.title || item.schedule2_title || item.id;
      });
    });
  }

  return map;
});

const getScope = async () => {
  loading.value = true;
  try {
    const response = await axios.get(
      `${apiEndpoint}/property/get-scope/${props.propertyId}`,
      headers
    );

    if (Object.keys(response.data.scope || {}).length !== 0) {
      const cleanedScope = {};
      Object.entries(response.data.scope).forEach(([sectionId, section]) => {
        cleanedScope[sectionId] = {
          ...section,
          items: (section.items || []).map((item) => ({
            ...item,
            item: cleanupTextFormat(item.item),
          })),
        };
      });
      scopeObj.value = cleanedScope;
    } else {
      scopeObj.value = {};
    }
  } catch (error) {
    toast.error("Failed to retrieve scope data");
    console.error("Error:", error);
  } finally {
    loading.value = false;
  }
};

const flatItems = computed(() => {
  const items = [];
  Object.entries(scopeObj.value || {}).forEach(([sectionId, section]) => {
    (section.items || []).forEach((item) => {
      if (item?.is_room) return;
      items.push({
        ...item,
        sectionId,
      });
    });
  });
  return items;
});

const tradeGroups = computed(() => {
  const map = new Map();

  const addItemToGroup = (codeVal, label, item) => {
    const key = codeVal ?? NO_TRADE_KEY;
    if (!map.has(key)) {
      map.set(key, {
        key,
        tradeCodeValue: codeVal,
        tradeCodeLabel: label,
        items: [],
      });
    }
    map.get(key).items.push(item);
  };

  flatItems.value.forEach((item) => {
    const codes = Array.isArray(item.trade_code)
      ? item.trade_code.filter(Boolean)
      : item.trade_code
        ? [item.trade_code]
        : [];

    if (!codes.length) {
      addItemToGroup(null, NO_TRADE_LABEL, item);
      return;
    }

    codes.forEach((code) => addItemToGroup(code, code, item));
  });

  const groups = Array.from(map.values()).map((group) => ({
    ...group,
    total: group.items.reduce((sum, curr) => sum + (Number(curr.cost) || 0), 0),
  }));

  groups.sort((a, b) => {
    const aNoTrade = a.tradeCodeValue === null;
    const bNoTrade = b.tradeCodeValue === null;
    if (aNoTrade && !bNoTrade) return 1;
    if (!aNoTrade && bNoTrade) return -1;
    return a.tradeCodeLabel.localeCompare(b.tradeCodeLabel);
  });

  return groups;
});

const tradeFilterOptions = computed(() => {
  const options = [{ label: "Filter by trade", value: "all" }];
  tradeGroups.value.forEach((group) => {
    options.push({ label: group.tradeCodeLabel, value: group.key });
  });
  return options;
});

const filteredGroups = computed(() => {
  if (selectedTrade.value === "all") return tradeGroups.value;
  return tradeGroups.value.filter((group) => group.key === selectedTrade.value);
});

const getSectionLabel = (item) => {
  const base = sectionTitleMap.value[item.sectionId] || item.sectionId;
  if (item.subitem && subsectionTitleMap.value[item.subitem]) {
    return `${base} — ${subsectionTitleMap.value[item.subitem]}`;
  }
  return base;
};

const visibleItems = (group) => {
  if (viewMoreState.value[group.key]) return group.items;
  return group.items.slice(0, ITEMS_LIMIT);
};

const toggleViewMore = (key) => {
  viewMoreState.value[key] = !viewMoreState.value[key];
};

onMounted(async () => {
  await getScope();
  if (props.setActions) {
    props.setActions({
      save: () => { },
      reset: () => { },
      print: () => { },
    });
  }
});

watch(
  () => props.propertyId,
  async (newVal, oldVal) => {
    if (newVal && newVal !== oldVal) {
      selectedTrade.value = "all";
      viewMoreState.value = {};
      await getScope();
    }
  }
);
</script>

<template>
  <LoadingSpinner v-if="loading" message="Loading scope..." :fullScreen="true" />

  <div class="trades-totals">
    <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3 gap-3">
      <div class="d-flex flex-column">
        <h5 class="mb-0 fwt-bold">Scope Trades Totals</h5>
        <p class="text-muted mb-0">Overview or drill down by trade code.</p>
      </div>
      <div class="d-flex align-items-center gap-2 flex-wrap justify-content-md-end">
        <div class="d-flex gap-3">

          <TradesFilter class="filter-compact" :options="tradeFilterOptions" v-model="selectedTrade" />
          
          <div class="btn-group view-toggle flex-shrink" role="group" aria-label="View switch">
            <button type="button" class="btn"
            :class="activeView === 'overview' ? 'btn-primary' : 'btn-outline-primary'" @click="activeView = 'overview'">
            Overview
          </button>
          <button type="button" class="btn"
          :class="activeView === 'items' ? 'btn-primary' : 'btn-outline-primary'" @click="activeView = 'items'">
          Items breakdown
        </button>
      </div>
        </div>
      </div>
    </div>

    <!-- Overview Table -->
    <div v-if="activeView === 'overview'" class="card shadow-sm">
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-hover mb-0">
            <thead class="table-light">
              <tr>
                <th style="width:70%">Trade</th>
                <th class="text-end" style="width:30%">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="group in filteredGroups" :key="group.key">
                <td class="fw-semibold">{{ group.tradeCodeLabel }}</td>
                <td class="text-end">{{ toCurrency(group.total) }}</td>
              </tr>
              <tr v-if="filteredGroups.length === 0">
                <td colspan="2" class="text-center text-muted py-3">No trades found for this filter.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Items breakdown -->
    <div v-else>
      <div v-if="!filteredGroups.length" class="alert alert-info">No scope items available for this property.</div>

      <div v-for="group in filteredGroups" :key="group.key" class="card shadow-sm mb-4">
      <div class="card-header trade-grid">
        <div class="d-flex align-items-center gap-2">
          <span class="badge bg-dark px-3 py-2 trade-title">{{ group.tradeCodeLabel }}</span>
          <span class="text-muted small">{{ group.items.length }} item{{ group.items.length === 1 ? "" : "s" }}</span>
        </div>
        <div></div>
        <div class="text-end fwt-bold cost-col total-align">{{ toCurrency(group.total) }}</div>
      </div>

      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-striped align-middle mb-0 trades-table">
            <colgroup>
              <col style="width: var(--col-item)" />
              <col style="width: var(--col-section)" />
              <col style="width: var(--col-cost)" />
            </colgroup>
            <thead class="table-light">
              <tr>
                <th class="item-col">Item</th>
                <th class="section-col">Section</th>
                <th class="cost-col text-end">Cost</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in visibleItems(group)" :key="`${group.key}-${idx}`">
                <td class="item-col">
                  <div class="fw-semibold">{{ item.item || "Untitled item" }}</div>
                  <div v-if="item.description" class="text-muted small">{{ item.description }}</div>
                </td>
                <td class="section-col">{{ getSectionLabel(item) }}</td>
                <td class="cost-col text-end">{{ toCurrency(item.cost) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="group.items.length > ITEMS_LIMIT" class="text-center py-3">
          <button class="btn btn-outline-primary btn-sm" @click="toggleViewMore(group.key)">
            {{ viewMoreState[group.key] ? "View less" : "View more" }}
          </button>
        </div>
      </div>

      <div class="card-footer trade-grid fwt-bold">
        <div></div>
        <div></div>
        <span class="cost-col text-end total-align">{{ toCurrency(group.total) }}</span>
      </div>
    </div>
    </div>
  </div>
</template>

<style scoped>
.trades-totals {
  min-height: 200px;
  width: 100%;
  margin: 0;
  --col-item: 60%;
  --col-section: 20%;
  --col-cost: 20%;
}

.filter-select {
  min-width: 240px;
}

.card-header {
  background-color: #f8f9fa;
}

.badge.bg-dark {
  letter-spacing: 0.03em;
}

.trade-title {
  font-size: 1.2rem;
}

.total-align {
  margin-left: auto;
}

.view-toggle .btn {
  min-width: 140px;
  white-space: nowrap;
}

.trades-table {
  table-layout: fixed;
}

.trades-table th,
.trades-table td {
  vertical-align: top;
}

.filter-compact {
  width: 180px;
}

.item-col {
  width: var(--col-item);
  overflow-wrap: anywhere;
  white-space: normal;
}

.section-col {
  width: var(--col-section);
  min-width: 160px;
  overflow-wrap: anywhere;
  white-space: normal;
}

.cost-col {
  width: var(--col-cost);
  min-width: 140px;
  padding-right: 12px;
}

.trade-grid {
  display: grid;
  grid-template-columns: var(--col-item) var(--col-section) var(--col-cost);
  align-items: center;
  gap: 0.5rem;
}
</style>
